import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/ui/button';
import { useToast } from '@/shared/hooks/use-toast';
import { useAuthStore } from '@/features/auth/store';
import { AuthLayout } from '../layouts/AuthLayout';
import { AuthFormLayout } from '../layouts/AuthFormLayout';
import { PhoneAuthFlow } from '../components/PhoneAuthFlow';

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const loginWithGoogle = useAuthStore((s) => s.loginWithGoogle);

  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [showPhone, setShowPhone] = useState(false);

  const handleGoogle = async () => {
    setIsGoogleLoading(true);
    try {
      const { needsProfileCompletion } = await loginWithGoogle();
      navigate(needsProfileCompletion ? '/complete-profile' : '/app');
    } catch (err: unknown) {
      const code = (err as { code?: string })?.code ?? '';
      if (code !== 'auth/popup-closed-by-user') {
        toast({ title: t('common.error'), description: t('auth.register.errors.googleFailed'), variant: 'destructive' });
      }
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handlePhoneSuccess = (_isNewUser: boolean, needsProfileCompletion: boolean) => {
    navigate(needsProfileCompletion ? '/complete-profile' : '/app');
  };

  const handlePhoneError = (message: string) => {
    toast({ title: t('common.error'), description: message, variant: 'destructive' });
  };

  return (
    <AuthLayout
      branding={{
        title: t('auth.register.brandingTitle'),
        subtitle: t('auth.register.brandingSubtitle'),
      }}
    >
      <AuthFormLayout
        title={t('auth.register.title')}
        subtitle={t('auth.register.subtitle')}
      >
        <div className="space-y-3">
          {!Capacitor.isNativePlatform() && (
            <Button
              type="button"
              variant="outline"
              className="w-full h-12 gap-3 font-medium"
              onClick={handleGoogle}
              disabled={isGoogleLoading}
            >
              {isGoogleLoading ? (
                <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <GoogleIcon />
              )}
              {t('auth.register.continueWithGoogle')}
            </Button>
          )}

          <Button
            type="button"
            variant="outline"
            className="w-full h-12 gap-3 font-medium"
            onClick={() => setShowPhone((p) => !p)}
            disabled={isGoogleLoading}
          >
            <Phone className="w-5 h-5 text-muted-foreground" />
            {t('auth.register.continueWithPhone')}
          </Button>
        </div>

        {showPhone && (
          <div className="mt-4 p-4 rounded-xl border border-border bg-muted/30">
            <PhoneAuthFlow
              submitLabel={t('auth.register.submit')}
              onSuccess={handlePhoneSuccess}
              onError={handlePhoneError}
            />
          </div>
        )}

        <p className="text-center text-sm text-muted-foreground mt-8">
          {t('auth.register.hasAccount')}{' '}
          <Link to="/login" className="text-accent font-medium hover:underline">
            {t('auth.register.loginLink')}
          </Link>
        </p>
      </AuthFormLayout>
    </AuthLayout>
  );
};

export default Register;
